import React from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Image from '@atlaskit/image';
import { Inline, Pressable, Stack } from '@atlaskit/primitives/compiled';
import { Radio } from '@atlaskit/radio';
import { token } from '@atlaskit/tokens';
import { CollapsibleItem } from '@atlassian/company-hub-common';
import {
	centerAlignment,
	leftAlignment,
	rightAlignment,
} from '@atlassian/company-hub-common/assets';

import type { LinksMenuExtensionActions } from '../../../../controllers/use-links-menu-parameters-and-actions';
import { ALIGNMENT } from '../../../../types';

import { messages } from './messages';

const styles = cssMap({
	options: {
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.neutral.subtle'),
		'&:hover': {
			backgroundColor: token('color.background.accent.blue.subtlest.hovered'),
			borderColor: token('color.border.selected'),
		},
	},
});

export type AlignmentConfigProps = {
	alignment: ALIGNMENT;
	updateAlignment: LinksMenuExtensionActions['updateAlignment'];
};

export const AlignmentConfig: React.FC<AlignmentConfigProps> = ({ alignment, updateAlignment }) => {
	const { formatMessage } = useIntl();
	const alignmentOptions = [
		{
			option: ALIGNMENT.CENTER,
			label: messages.centerOption,
			image: centerAlignment,
		},
		{
			option: ALIGNMENT.RIGHT,
			label: messages.rightOption,
			image: rightAlignment,
		},
		{
			option: ALIGNMENT.LEFT,
			label: messages.leftOption,
			image: leftAlignment,
		},
	];

	return (
		<CollapsibleItem title={formatMessage(messages.alignmentSectionTitle)}>
			<Stack space="space.200">
				{alignmentOptions.map(({ option, label, image }) => (
					<Pressable key={option} xcss={styles.options} onClick={() => updateAlignment(option)}>
						<Inline spread="space-between" alignBlock="center">
							<Radio
								value="default radio"
								label={formatMessage(label)}
								name={`radio-${option}`}
								testId={`radio-${option}${alignment === option ? '--selected' : ''}`}
								isChecked={alignment === option}
							/>
							<Image src={image} alt="" />
						</Inline>
					</Pressable>
				))}
			</Stack>
		</CollapsibleItem>
	);
};
